'use client';

import { c as _c } from "react/compiler-runtime";
import { Popover, PopoverButton, PopoverBackdrop, PopoverPanel } from '@headlessui/react';
import { cn } from '@/lib/utils';
import { startThemeTransition } from '@/lib/theme-transition';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTheme } from 'next-themes';
import { useEffect, useRef } from 'react';
import { useIsClientMounted } from '@/hooks/useIsClientMounted';
import { m } from 'motion/react';
import { Container } from '@/components/Container';
import { useWebHaptics } from 'web-haptics/react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PRIMARY_NAV_VISIBLE } from '@/lib/nav';
import avatarImage from '@/images/avatar.jpg';
function CloseIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="m17.25 6.75-10.5 10.5M6.75 6.75l10.5 10.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function HamburgerIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function SunIcon(props) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M8 12.25A4.25 4.25 0 0 1 12.25 8v0a4.25 4.25 0 0 1 4.25 4.25v0a4.25 4.25 0 0 1-4.25 4.25v0A4.25 4.25 0 0 1 8 12.25v0Z" />;
    t1 = <path d="M12.25 3v1.5M21.5 12.25H20M18.79 18.79l-1.06-1.06M18.79 5.71l-1.06 1.06M12.25 20v1.5M4.5 12.25H3M6.77 6.77 5.71 5.71M6.77 17.73l-1.06 1.06" fill="none" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <svg viewBox="0 0 24 24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>{t0}{t1}</svg>;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function MoonIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M17.25 16.22a6.94 6.94 0 0 1-9.47-9.47 7.45 7.45 0 1 0 9.47 9.47ZM12.75 7C17 7 17 2.75 17 2.75S17 7 21.25 7C17 7 17 11.25 17 11.25S17 7 12.75 7Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function MobileNavItem(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== href) {
    t1 = <li><PopoverButton as={Link} href={href} className="block py-2">{children}</PopoverButton></li>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function MobileNavigation(props) {
  const $ = _c(6);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PopoverButton aria-label="Open navigation menu" className="group flex items-center rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-zinc-800 shadow-lg ring-1 shadow-zinc-800/5 ring-zinc-900/5 backdrop-blur-sm dark:bg-zinc-800/90 dark:text-zinc-200 dark:ring-white/10 dark:hover:ring-white/20"><HamburgerIcon className="size-5 stroke-zinc-500 group-hover:stroke-zinc-700 dark:group-hover:stroke-zinc-400" /></PopoverButton>;
    t1 = <PopoverBackdrop transition={true} className="fixed inset-0 z-50 bg-zinc-800/40 backdrop-blur-xs transition-opacity duration-150 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in dark:bg-neutral-950/80" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-row-reverse items-center justify-between"><PopoverButton aria-label="Close menu" className="-m-1 p-1"><CloseIcon className="size-6 text-zinc-500 dark:text-zinc-400" /></PopoverButton><h2 className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Navigation</h2></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PopoverPanel focus={true} transition={true} className="fixed inset-x-4 top-8 z-50 origin-top rounded-3xl bg-white p-8 ring-1 ring-zinc-900/5 transition-[opacity,transform] duration-150 data-closed:scale-95 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in dark:bg-zinc-900 dark:ring-zinc-800">{t2}<nav className="mt-6"><ul className="-my-2 divide-y divide-zinc-100 text-base text-zinc-800 dark:divide-zinc-100/5 dark:text-zinc-300">{PRIMARY_NAV_VISIBLE.map(_temp)}</ul></nav></PopoverPanel>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== props) {
    t4 = <Popover {...props}>{t0}{t1}{t3}</Popover>;
    $[4] = props;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function _temp(link) {
  return <MobileNavItem key={link.href} href={link.href}>{link.label}</MobileNavItem>;
}
function NavItem(t0) {
  const $ = _c(9);
  const {
    href,
    children
  } = t0;
  const pathname = usePathname();
  const mounted = useIsClientMounted();
  const isActive = mounted && pathname === href;
  const t1 = isActive ? "text-teal-500 dark:text-teal-400" : "hover:text-teal-500 dark:hover:text-teal-400";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("relative block px-3 py-2 transition", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isActive) {
    t3 = isActive && <span className="absolute inset-x-1 -bottom-px h-px bg-linear-to-r from-teal-500/0 via-teal-500/40 to-teal-500/0 dark:from-teal-400/0 dark:via-teal-400/40 dark:to-teal-400/0" />;
    $[2] = isActive;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== href || $[6] !== t2 || $[7] !== t3) {
    t4 = <li><Link href={href} className={t2}>{children}{t3}</Link></li>;
    $[4] = children;
    $[5] = href;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function DesktopNavigation(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul className="flex rounded-full bg-white/90 px-3 text-sm font-medium text-zinc-800 shadow-lg ring-1 shadow-zinc-800/5 ring-zinc-900/5 backdrop-blur-sm dark:bg-zinc-800/90 dark:text-zinc-200 dark:ring-white/10">{PRIMARY_NAV_VISIBLE.map(_temp2)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <nav {...props}>{t0}</nav>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp2(link) {
  return <NavItem key={link.href} href={link.href}>{link.label}</NavItem>;
}
export function ThemeToggle() {
  const $ = _c(17);
  const {
    resolvedTheme,
    setTheme
  } = useTheme();
  const otherTheme = resolvedTheme === "dark" ? "light" : "dark";
  const mounted = useIsClientMounted();
  const {
    track
  } = useAnalytics();
  const haptic = useWebHaptics();
  let t0;
  if ($[0] !== haptic || $[1] !== otherTheme || $[2] !== setTheme || $[3] !== track) {
    t0 = e => {
      startThemeTransition(() => setTheme(otherTheme), e);
      haptic.trigger("light");
      track("theme_toggle", {
        theme: otherTheme
      });
    };
    $[0] = haptic;
    $[1] = otherTheme;
    $[2] = setTheme;
    $[3] = track;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleThemeToggle = t0;
  if (!mounted) {
    return null;
  }
  const t1 = mounted ? `Switch to ${otherTheme} theme` : "Toggle theme";
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      rotate: 0
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = resolvedTheme === "dark" ? 0 : 180;
  let t4;
  if ($[6] !== t3) {
    t4 = {
      rotate: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      type: "spring",
      stiffness: 200,
      damping: 10
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SunIcon className="size-6 fill-zinc-100 stroke-zinc-500 transition group-hover:fill-zinc-200 group-hover:stroke-zinc-700 dark:hidden [@media(prefers-color-scheme:dark)]:fill-teal-50 [@media(prefers-color-scheme:dark)]:stroke-teal-500 [@media(prefers-color-scheme:dark)]:group-hover:fill-teal-50 [@media(prefers-color-scheme:dark)]:group-hover:stroke-teal-600" />;
    t7 = <MoonIcon className="hidden size-6 fill-zinc-700 stroke-zinc-500 transition dark:block [@media_not_(prefers-color-scheme:dark)]:fill-teal-400/10 [@media_not_(prefers-color-scheme:dark)]:stroke-teal-500 [@media(prefers-color-scheme:dark)]:group-hover:stroke-zinc-400" />;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4) {
    t8 = <m.div initial={t2} animate={t4} transition={t5} className="flex items-center justify-center">{t6}{t7}</m.div>;
    $[11] = t4;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== handleThemeToggle || $[14] !== t1 || $[15] !== t8) {
    t9 = <button type="button" aria-label={t1} className="group ease-out-strong rounded-full bg-white/90 px-3 py-2 shadow-lg ring-1 shadow-zinc-800/5 ring-zinc-900/5 backdrop-blur-sm transition duration-150 active:scale-95 dark:bg-zinc-800/90 dark:ring-white/10 dark:hover:ring-white/20" onClick={handleThemeToggle}>{t8}</button>;
    $[13] = handleThemeToggle;
    $[14] = t1;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
function clamp(number: number, a: number, b: number) {
  const min = Math.min(a, b);
  const max = Math.max(a, b);
  return Math.min(Math.max(number, min), max);
}
function AvatarContainer(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className, "size-10 rounded-full bg-white/90 p-0.5 shadow-lg ring-1 shadow-zinc-800/5 ring-zinc-900/5 backdrop-blur-sm md:h-10 md:w-10 dark:bg-zinc-800/90 dark:ring-white/10");
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function Avatar(t0) {
  const $ = _c(15);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      large: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const large = t1 === undefined ? false : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn(className, "pointer-events-auto");
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = large ? "4rem" : "2.25rem";
  const t4 = large ? "size-16" : "size-full";
  let t5;
  if ($[6] !== t4) {
    t5 = cn("rounded-full bg-zinc-100 object-cover dark:bg-zinc-800", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <Image src={avatarImage} alt="Arne Noori" sizes={t3} className={t5} priority={true} />;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== props || $[12] !== t2 || $[13] !== t6) {
    t7 = <Link href="/" aria-label="Home" className={t2} {...props}>{t6}</Link>;
    $[11] = props;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
export function Header() {
  const $ = _c(11);
  const pathname = usePathname();
  const headerRef = useRef(null);
  const avatarRef = useRef(null);
  const isInitial = useRef(true);
  const isHomePage = pathname === "/";
  let t0;
  let t1;
  if ($[0] !== isHomePage) {
    t0 = () => {
      if (!isHomePage) {
        return;
      }
      const downDelay = avatarRef.current?.offsetTop ?? 0;
      const writeRootProperty = function writeRootProperty(property, value) {
        document.documentElement.style.setProperty(property, value);
      };
      const removeProperty = function removeProperty(property_0) {
        document.documentElement.style.removeProperty(property_0);
      };
      const updateHeaderStyles = function updateHeaderStyles() {
        if (!headerRef.current) {
          return;
        }
        const {
          top,
          height
        } = headerRef.current.getBoundingClientRect();
        const scrollY = clamp(window.scrollY, 0, document.body.scrollHeight - window.innerHeight);
        if (isInitial.current) {
          writeRootProperty("--header-position", "sticky");
        }
        writeRootProperty("--content-offset", `${downDelay}px`);
        if (isInitial.current || scrollY < downDelay) {
          writeRootProperty("--header-height", `${downDelay + height}px`);
          writeRootProperty("--header-mb", `${-downDelay}px`);
        } else {
          if (top + height < -64) {
            const offset = Math.max(height, scrollY - 64);
            writeRootProperty("--header-height", `${offset}px`);
            writeRootProperty("--header-mb", `${height - offset}px`);
          } else {
            if (top === 0) {
              writeRootProperty("--header-height", `${scrollY + height}px`);
              writeRootProperty("--header-mb", `${-scrollY}px`);
            }
          }
        }
        if (top === 0 && scrollY > 0 && scrollY >= downDelay) {
          writeRootProperty("--header-inner-position", "fixed");
          removeProperty("--header-top");
          removeProperty("--avatar-top");
        } else {
          removeProperty("--header-inner-position");
          writeRootProperty("--header-top", "0px");
          writeRootProperty("--avatar-top", "0px");
        }
      };
      const updateAvatarStyles = function updateAvatarStyles() {};
      const updateStyles = function updateStyles() {
        updateHeaderStyles();
        updateAvatarStyles();
        isInitial.current = false;
      };
      updateStyles();
      window.addEventListener("scroll", updateStyles, {
        passive: true
      });
      window.addEventListener("resize", updateStyles, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", updateStyles);
        window.removeEventListener("resize", updateStyles);
        removeProperty("--header-position");
        removeProperty("--content-offset");
        removeProperty("--header-height");
        removeProperty("--header-mb");
        removeProperty("--header-inner-position");
        removeProperty("--header-top");
        removeProperty("--avatar-top");
      };
    };
    t1 = [isHomePage];
    $[0] = isHomePage;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      height: "var(--header-height, 11.25rem)",
      marginBottom: "var(--header-mb, -7.25rem)"
    };
    t3 = <div ref={avatarRef} className="header-spacer-anchor order-last mt-[calc(--spacing(16)-(--spacing(3)))]" />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "var(--header-position)" as React.CSSProperties['position']
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      position: "var(--header-inner-position)" as React.CSSProperties['position']
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex flex-1"><AvatarContainer className="header-avatar"><Avatar /></AvatarContainer></div>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex flex-1 justify-end md:justify-center"><MobileNavigation className="pointer-events-auto md:hidden" /><DesktopNavigation className="pointer-events-auto hidden md:block" /></div>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <header className="site-header header-shell pointer-events-none relative z-50 flex flex-none flex-col" style={t2}>{t3}<div ref={headerRef} className="top-0 z-10 h-16 pt-6 md:h-16" style={t4}><Container className="top-(--header-top,--spacing(6)) w-full" style={t5}><div className="relative flex gap-4">{t6}{t7}<div className="flex justify-end md:flex-1"><div className="header-theme-toggle pointer-events-auto"><ThemeToggle /></div></div></div></Container></div></header>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <>{t8}<div className="header-content-offset flex-none" style={{
        height: "var(--content-offset, 7.25rem)"
      }} /></>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  return t9;
}